// ─── src/pages/H7.jsx ───────────────────────────────────────────────────────
// Duplicate of H3.jsx, mounted at /h7 for side-by-side experimentation.
// No edits to behaviour or styling, identical to H3.
// To diverge, edit this file freely; H3.jsx remains the route at /h3.
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { resolveIdentity } from "../seo/identityEngine.js";
import { useTheme } from "../theme/ThemeContext";

// Homepage identity is owned by the Search Identity Engine (single source of truth).
const HOME_SEO = resolveIdentity("/");
import { useAura } from "../stores/auraCore";
import { FEATURED_VENUES } from "../data/featuredVenues";
import { VENDOR_CATEGORIES } from "../data/geo";
import { CategoryCarousel } from "./LocationPage";
import { fetchPublicListings, CARD_COLUMNS } from "../services/listings";
import { isVenueListing } from "../utils/listingClassification";
import { imgUrl } from "../utils/imgUrl.js";
import { transformListing } from "../utils/transformListing";
// Session-free client, this page's queries (listings, magazine_issues) are all
// public/published content and must behave identically for anonymous and
// signed-in visitors. Never import the session-locked `supabase` client here.
import { supabaseAnon } from "../lib/supabaseClient";

// Above-the-fold: eager so the hero paints on first render (LCP path).
import HomeNav from "../components/nav/HomeNav";
import SlimHero from "../components/sections/SlimHero";
import MasterCategoryCard, { LUXURY_ICONS } from "../components/cards/MasterCategoryCard";

// Below-the-fold sections + overlays: lazy so their JS (sliders, grids, venue/
// vendor cards, immersive search) is not in the homepage's initial critical path.
// All sit 2+ viewport heights down (after the hero and the full-height Aura altar),
// so they stream in before the visitor scrolls to them.
const FeaturedSlider   = lazy(() => import("../components/sections/FeaturedSlider"));
const DestinationGrid  = lazy(() => import("../components/sections/DestinationGrid"));
const VenueGrid        = lazy(() => import("../components/sections/VenueGrid"));
const VendorPreview    = lazy(() => import("../components/sections/VendorPreview"));
const DirectoryBrands  = lazy(() => import("../components/sections/DirectoryBrands"));
const NewsletterBand   = lazy(() => import("../components/sections/NewsletterBand"));
const MagazineEditorial= lazy(() => import("../components/sections/MagazineEditorial"));
const EnquiryModal     = lazy(() => import("../components/modals/EnquiryModal"));
const ImmersiveSearch  = lazy(() => import("../components/search/ImmersiveSearch"));
import "../category.css";

// ─── Font tokens ──────────────────────────────────────────────────────────────
const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

// ── Category hover images ──────────────────────────────────────────────────────

// ─── Luxury category icons ────────────────────────────────────────────────────
// MOVED to src/components/cards/MasterCategoryCard.jsx (shared master component)

// ─── Mapper: Supabase Listing → card-compatible shape ─────────────────────────
// Called after fetchListings() which already runs buildCardImgs + buildCardVideoUrl,
// so imgs[] and videoUrl are pre-built rich objects, no further media work needed.
function listingToCard(listing) {
  const base = transformListing(listing);
  return {
    ...base,
    // type is normalised to "venue"|"vendor" by transformListing, no override needed
    // isVenue: H7-specific helper used by goVenue routing
    isVenue: isVenueListing(listing.categorySlug),
    // Rich image objects, preserve originals for gallery + photographer credits
    imgs: (() => {
      const raw = listing.imgs || [];
      if (raw.length > 0) return raw;
      if (listing.heroImage) return [{ src: listing.heroImage, url: listing.heroImage, alt_text: '', is_featured: true, sort_order: 0 }];
      return [];
    })(),
    videoUrl:      listing.videoUrl || null,
    // Snake-case slug aliases for LuxuryVendorCard URL building
    country_slug:  listing.countrySlug  || listing.country_slug  || '',
    region_slug:   listing.regionSlug   || listing.region_slug   || '',
    category_slug: listing.categorySlug || listing.category_slug || '',
    // Quality tier & editorial
    contentScore:         listing.contentQualityScore ?? null,
    editorialApproved:    listing.editorialApproved   ?? false,
    editorialFactChecked: listing.editorialFactChecked ?? false,
    // Bridal boutique feature flag
    bridalVirtualTryon: listing.bridalVirtualTryon ?? false,
    updatedAt:   listing.updatedAt   || listing.updated_at   || null,
    publishedAt: listing.publishedAt || listing.published_at || null,
  };
}

// Category carousel: shared with LocationPage.jsx (one source of truth for the
// category-card grid, including its onLightBg treatment for arrows/dots on a
// cream section background).

export default function H7({ onViewVenue, onViewCategory, onViewRegion, onViewRegionCategory, goCountry, goRegion, goAllDestinations, onViewStandard, onViewAbout, onViewContact, onViewPartnership, onViewVendor, onViewAdmin, onViewUSA, onViewItaly, onViewCountry, onViewMagazine, onViewMagazineArticle, onViewAtlas, onViewPublication, onViewRealWedding, onViewSearch, footerNav }) {
  const themeCtx = useTheme();
  const darkMode = themeCtx.darkMode;
  const C = themeCtx;
  const [enquiryVendor, setEnquiryVendor] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [hovCat, setHovCat] = useState(null);
  const [dbListings, setDbListings] = useState([]);
  // Signature Collection is a CURATED (is_featured) set, fetched separately from the
  // recency-limited homepage feed. Without this, building many new venues pushes the
  // older curated venues out of the 40-row recent window and the slider starves to one.
  const [signatureListings, setSignatureListings] = useState([]);
  // Vendors, fetched separately too. The recency feed is dominated by venues (242 vs 32
  // vendors), so building many venues starves the vendor preview + the category badges.
  const [vendorListings, setVendorListings] = useState([]);
  // Venues, fetched separately for the same reason in reverse: a bulk vendor/planner
  // update can starve the venue grid instead. See the fetch effect below.
  const [venueListings, setVenueListings] = useState([]);
  // Never auto-open the immersive overlay on a fresh page load, it must not take
  // over the homepage. The user opens it deliberately (search bar / prompts); a
  // reload or new visit always lands on the homepage itself. (Regression: this used
  // to restore an "open" flag from sessionStorage and re-hijack the homepage on
  // every refresh until the tab/site data was cleared.)
  const [immersiveOpen, setImmersiveOpen] = useState(false);
  const [immersiveQuery, setImmersiveQuery] = useState(null);
  const setChatContext = useAura((s) => s.setChatContext);

  // Purge the legacy persisted-open flag so already-affected browsers heal on the
  // next load instead of reopening the takeover.
  useEffect(() => {
    try { sessionStorage.removeItem('lwd:immersive-open'); } catch {}
  }, []);

  // Mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Set chat context on mount
  useEffect(() => {
    setChatContext?.({ page: "home" });
  }, [setChatContext]);

  // Fetch live published listings from Supabase
  // Falls back gracefully: VenueGrid uses FEATURED_VENUES, VendorPreview uses GLOBAL_VENDORS
  // limit:40, this only ever renders a handful of cards in horizontal sliders;
  // unbounded (all ~200+ published listings, each carrying the media_items JSONB
  // blob) was measured at 11s / 718KB on the real homepage query. 40 rows,
  // recency-ordered (most recently updated/published first), comfortably covers
  // both the venue and vendor grids (confirmed ~24/16 split in the first 40 rows).
  useEffect(() => {
    fetchPublicListings({ columns: CARD_COLUMNS, limit: 40, sort: 'recent' })
      .then((listings) => setDbListings(listings))
      .catch(() => {}); // silent fail, static fallbacks stay active
  }, []);

  // Signature Collection, its own query. sort:'default' orders is_featured-first,
  // so the curated venues come back regardless of how many new listings exist.
  useEffect(() => {
    fetchPublicListings({ columns: CARD_COLUMNS, category_slug: 'wedding-venues', sort: 'default', limit: 16 })
      .then((listings) => setSignatureListings(listings))
      .catch(() => {});
  }, []);

  // Vendors, dedicated non-venue query so the vendor preview + category badges are
  // not starved by the venue-heavy recency feed. sort:'recent' keeps the same "latest
  // vendors" feel the homepage had before venues flooded the shared feed.
  useEffect(() => {
    fetchPublicListings({ columns: CARD_COLUMNS, vendorsOnly: true, sort: 'recent', limit: 40 })
      .then((listings) => setVendorListings(listings))
      .catch(() => {});
  }, []);

  // Venues, dedicated query, same reasoning in reverse: a bulk vendor/planner update
  // (e.g. a backfill touching updated_at on dozens of planner rows) can fill the entire
  // shared 40-row recent feed with non-venues, starving VenueGrid to zero and silently
  // triggering the FEATURED_VENUES fallback even though real venues exist. Confirmed
  // live 2026-08-25: a planner portrait backfill left 0 of the top 40 recent rows as
  // venues. category_slug filter here mirrors the vendorsOnly query above.
  useEffect(() => {
    fetchPublicListings({ columns: CARD_COLUMNS, category_slug: 'wedding-venues', sort: 'recent', limit: 40 })
      .then((listings) => setVenueListings(listings))
      .catch(() => {});
  }, []);

  // ── Derive live venue + vendor arrays from DB listings ──────────────────────
  // venue = listingType "venue"; everything else (photographer, planner, etc.) = vendor
  // If DB has no data yet, fall back to static curated data automatically.
  const dbCards       = dbListings.map(listingToCard);
  // Prefer the dedicated venue query; fall back to whatever venues are in the shared
  // recent feed. Sort by published_at (true "latest listing" order), updated_at is
  // unreliable because bulk maintenance (e.g. portrait/pricing-model backfills)
  // restamps it across many listings.
  const venueCards    = venueListings.map(listingToCard).filter((c) => c.isVenue)
    .sort((a, b) => new Date(b.publishedAt || b.updatedAt || 0) - new Date(a.publishedAt || a.updatedAt || 0));
  const dbVenueCards  = venueCards.length > 0 ? venueCards : dbCards.filter((c) => c.isVenue)
    .sort((a, b) => new Date(b.publishedAt || b.updatedAt || 0) - new Date(a.publishedAt || a.updatedAt || 0));
  // Prefer the dedicated vendor query; fall back to whatever vendors are in the recent feed.
  // Sort by published_at (true "latest listing" order), updated_at is unreliable because
  // bulk maintenance (e.g. video-credit backfills) restamps it across many listings.
  const vendorCards   = vendorListings.map(listingToCard).filter((c) => !c.isVenue)
    .sort((a, b) => new Date(b.publishedAt || b.updatedAt || 0) - new Date(a.publishedAt || a.updatedAt || 0));
  const dbVendorCards = vendorCards.length > 0 ? vendorCards : dbCards.filter((c) => !c.isVenue);

  // VenueGrid: prefer live DB venues, fall back to FEATURED_VENUES
  const displayVenues = dbVenueCards.length > 0 ? dbVenueCards : FEATURED_VENUES;
  // VendorPreview handles its own fallback to GLOBAL_VENDORS when dbVendors is empty/null
  const displayVendors = dbVendorCards.length > 0 ? dbVendorCards : null;
  // Signature Collection: curated is_featured venues from the dedicated query, then
  // the recent-feed featured venues, then the static set, so it never starves to one.
  const featuredSliderVenues = (() => {
    const sig = signatureListings.map(listingToCard).filter(c => c.isVenue && c.featured && c.imgs?.length > 0);
    if (sig.length > 0) return sig;
    const live = dbVenueCards.filter(c => c.featured && c.imgs?.length > 0);
    if (live.length > 0) return live;
    return FEATURED_VENUES;
  })();

  // Set of category slugs that have ≥1 live published listing globally.
  // Drives the "Coming Soon" badge on empty category cards.
  const activeCategorySlugs = (() => {
    if (dbListings.length === 0) return null; // still loading, don't show badges yet
    const s = new Set();
    if (dbVenueCards.length > 0) s.add("wedding-venues");
    dbVendorCards.forEach((c) => { if (c.cat) s.add(c.cat); });
    return s;
  })();

  // Aura "altar" palette, lights-aware so the signature section can be tried on cream.
  const aura = darkMode ? {
    bg: "radial-gradient(circle at center, #0e0d0b 0%, #090807 60%, #020202 100%)",
    ink: "#f5f0e8", body: "rgba(245,240,232,0.62)", gold: "#d39a21",
    eyebrow: "rgba(211,154,33,0.75)", watermark: "rgba(196,181,157,0.032)",
    btnBorder: "rgba(211,154,33,0.55)", btnHoverText: "#0a0806",
  } : {
    bg: "radial-gradient(circle at center, #fbfaf7 0%, #f9f8f4 58%, #f1efe7 100%)",
    ink: "#26282d", body: "#5a6068", gold: "#b8860b",
    eyebrow: "#b8860b", watermark: "rgba(40,38,32,0.05)",
    btnBorder: "rgba(184,134,11,0.5)", btnHoverText: "#ffffff",
  };

  return (
      <div style={{ background: C.black, minHeight: "100vh" }}>
        <Helmet>
          <title>{HOME_SEO.title}</title>
          <meta name="description" content="Discover the world's finest luxury wedding venues, planners and vendors. Handpicked by LWD for couples planning exceptional weddings in Italy, France, the UK and beyond." />
          <link rel="canonical" href={HOME_SEO.canonical} />
          <meta property="og:title"       content={HOME_SEO.title} />
          <meta property="og:description" content="Discover the world's finest luxury wedding venues, planners and vendors. Handpicked for couples planning exceptional weddings." />
          <meta property="og:url"         content="https://luxuryweddingdirectory.com/" />
          <meta property="og:type"        content="website" />
          <meta property="og:image"       content="https://luxuryweddingdirectory.com/og-default.jpg" />
          <meta name="twitter:card"        content="summary_large_image" />
          <meta name="twitter:title"       content="Luxury Wedding Directory" />
          <meta name="twitter:description" content="Discover the world's finest luxury wedding venues, planners and vendors." />
          <meta name="twitter:image"       content="https://luxuryweddingdirectory.com/og-default.jpg" />
        </Helmet>
        <HomeNav
          darkMode={darkMode}
          onToggleDark={themeCtx.toggleDark}
          onVendorLogin={() => onViewVendor?.()}
          onNavigateStandard={onViewStandard}
          onNavigateAbout={onViewAbout}
        />

        <main>
          {/* ═══ 1. HERO ═══════════════════════════════════════════════════════
              Authoritative, not descriptive. "AI-Powered Wedding Discovery"
              and "World's Finest Wedding Directory" both read as marketplace
              positioning. The H7 variant claims a posture instead: this is
              not a marketplace, it's an editorial standard, by invitation. */}
          <SlimHero
            onViewRegion={onViewRegion}
            onViewRegionCategory={onViewRegionCategory}
            onViewCategory={onViewCategory}
            onViewCountry={onViewCountry}
            onViewVenue={onViewVenue}
            onViewEditArticle={onViewMagazineArticle}
            onViewAtlas={onViewAtlas}
            onViewPublication={onViewPublication}
            onViewRealWedding={onViewRealWedding}
            onViewSearch={onViewSearch}
            onOpenImmersive={(q) => { setImmersiveQuery(q || null); setImmersiveOpen(true); }}
            eyebrow=""
            headlineLine1="Wedding"
            headlineLine2="Intelligence"
            supportLine="Curating exceptional weddings since 2006"
          />

          {/* ═══ 2. AURA, Signature feature, the brain of the platform ═════════ */}
          <section
            style={{
              background: aura.bg,
              // Desktop: a full-height "altar". Mobile: size to content (+ padding)
              // so it isn't a ~940px near-empty band on a phone.
              minHeight: isMobile ? "auto" : "calc(100vh + 100px)",
              padding: isMobile ? "72px 24px" : "80px clamp(40px, 6vw, 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => setImmersiveOpen(true)}
          >
            {/* Layered ambient glows */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle, rgba(211,154,33,0.07) 0%, transparent 55%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", top: -120, right: -100, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(211,154,33,0.09) 0%, transparent 60%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: -140, left: -80, width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle, rgba(211,154,33,0.05) 0%, transparent 55%)", pointerEvents: "none" }} />

            {/* Watermark */}
            <span aria-hidden="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -55%)", fontFamily: GD, fontSize: "clamp(200px, 28vw, 340px)", fontWeight: 400, fontStyle: "italic", color: aura.watermark, whiteSpace: "nowrap", pointerEvents: "none", userSelect: "none", lineHeight: 1, zIndex: 0, letterSpacing: "-0.04em" }}>
              Aura
            </span>

            {/* Central glow behind content */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(211,154,33,0.08) 0%, transparent 60%)", pointerEvents: "none", zIndex: 0 }} />
            <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
              {/* Decorative diamond */}
              <div style={{ marginBottom: 32 }}>
                <span style={{ display: "inline-block", width: 8, height: 8, background: aura.gold, transform: "rotate(45deg)", opacity: 0.7 }} />
              </div>
              <p style={{ fontFamily: NU, fontSize: 9, letterSpacing: "0.4em", color: aura.eyebrow, textTransform: "uppercase", margin: "0 0 32px" }}>
                Powered by Aura
              </p>
              <h2 style={{ fontFamily: GD, fontSize: "clamp(58px, 9vw, 112px)", fontWeight: 400, color: aura.ink, lineHeight: 0.98, margin: "0 0 36px", letterSpacing: "-0.03em" }}>
                Tell us your{" "}
                <em style={{ fontStyle: "italic", color: aura.gold }}>vision</em>.
              </h2>
              <p style={{ fontFamily: NU, fontSize: "clamp(15px, 1.6vw, 19px)", color: aura.body, lineHeight: 1.6, margin: "0 auto 56px", maxWidth: 560, fontWeight: 300, fontStyle: "italic" }}>
                Describe your celebration. Aura introduces the venues, destinations and creatives that fit.
              </p>
              <button
                onClick={(e) => { e.stopPropagation(); setImmersiveOpen(true); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 12,
                  display: "inline-flex", alignItems: "center", gap: 12,
                  background: "transparent",
                  border: `1px solid ${aura.btnBorder}`,
                  borderRadius: 1,
                  padding: "16px 36px",
                  color: aura.gold,
                  fontFamily: NU,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.30em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  transition: "background 0.25s ease, border-color 0.25s ease, color 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = aura.gold;
                  e.currentTarget.style.borderColor = aura.gold;
                  e.currentTarget.style.color = aura.btnHoverText;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderColor = aura.btnBorder;
                  e.currentTarget.style.color = aura.gold;
                }}
              >
                Speak with Aura
                <span aria-hidden style={{
                  width: 5, height: 5, background: "currentColor",
                  transform: "rotate(45deg)", flexShrink: 0,
                }} />
              </button>
            </div>
          </section>

          {/* ═══ 2b. FIND YOUR TEAM, Category Shortcuts ═══════════════════════
              Same carousel used on country/region pages (LocationPage.jsx),
              exported from there so there's one source of truth for the
              category-card grid. Sitewide here, so no location name in the
              heading and no "coming soon" dimming — every category routes to
              its global browse page (goCategory with no countrySlug). */}
          <section
            aria-label="Browse by category"
            className="lwd-region-categories"
            style={{ padding: isMobile ? "40px 20px 56px" : "52px 48px 72px" }}
          >
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 48, width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 1, background: C.gold }} />
                  <span style={{ fontFamily: NU, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>Find Your Team</span>
                  <div style={{ width: 28, height: 1, background: C.gold }} />
                </div>
                <h2 style={{ fontFamily: GD, fontSize: isMobile ? "clamp(22px, 6vw, 30px)" : "clamp(26px, 3vw, 36px)", fontWeight: 400, color: C.off, lineHeight: 1.2, margin: 0, textAlign: "center", width: "100%" }}>
                  <span style={{ color: C.grey }}>Wedding Venues</span>{" "}
                  <span style={{ fontStyle: "italic", color: C.white }}>and Vendors</span>
                </h2>
              </div>
              <CategoryCarousel
                categories={VENDOR_CATEGORIES}
                C={C}
                onLightBg={!darkMode}
                onSelect={(slug) => onViewCategory?.({ category: slug })}
                activeCategorySlugs={null}
                isMobile={isMobile}
              />
            </div>
          </section>

          {/* ═══ 3. DESTINATIONS, moved up to follow the Aura altar ═══════════
              Sits directly after "Tell us your vision" so the visitor's
              first scroll lands on geographic browse, the natural answer
              to the Aura prompt. Was section 5 in the original order. */}
          <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
          <DestinationGrid
            goCountry={goCountry}
            goRegion={goRegion}
            goAllDestinations={goAllDestinations}
            onDestinationClick={(d) => {
              if (d.countrySlug && d.regionSlug) {
                onViewRegion?.(d.countrySlug, d.regionSlug);
              }
            }}
          />

          {/* ═══ 4. SIGNATURE COLLECTION, Editorial cinematic moment ══════════
              Mobile caps the slider at 4 cards so the page doesn't drown
              in scrolling cards. Desktop sees the full set. */}
          <FeaturedSlider venues={isMobile ? featuredSliderVenues.slice(0, 4) : featuredSliderVenues} onViewVenue={(v) => onViewVenue?.(v)} />

          {/* ═══ 5. VENUES, The Collection (unified, no duplication) ══════════
              Same mobile cap applied, 4 venues max so the section feels
              curated, not a wall of cards. */}
          <VenueGrid venues={isMobile ? displayVenues.slice(0, 4) : displayVenues} onViewVenue={(v) => onViewVenue?.(v)} onViewCategory={onViewCategory} />

          {/* ═══ 6. AUTHORITY STATEMENT, institutional anchor ═════════════════
              The quiet, weighted positional beat that connects this homepage
              to the brand voice on /apply-for-inclusion and /about. Single
              italic line, gold rules above and below, attribution in tiny
              gold caps. Reads like a magazine epigraph, not a feature pitch. */}
          <AuthorityStatement isMobile={isMobile} darkMode={darkMode} />

          {/* ═══ 7. VENDORS, The Creatives ═══════════════════════════════════
              Mobile caps to 4 vendors max, same restraint as the venue
              grid above. Desktop sees the full set. */}
          <VendorPreview
            dbVendors={isMobile && displayVendors ? displayVendors.slice(0, 4) : displayVendors}
            onViewCategory={onViewCategory}
            onViewVendor={(v) => onViewVenue?.(v)}
          />

          {/* ═══ 8. THE LWD EDIT, articles teaser ═════════════════════════════════
              Same MagazineEditorial component as before, now wrapped in a
              proper editorial section header so it reads as part of the
              site's section vocabulary (gold-caps eyebrow + italic title). */}
          <section style={{ background: darkMode ? "#000000" : "#f9f8f4", padding: isMobile ? "80px 0 0" : "120px 0 0" }}>
            <SectionLabel eyebrow="The LWD Edit" title="Venues, weddings, and the details that matter" light={!darkMode} />
            <MagazineEditorial
              light={!darkMode}
              onViewMagazine={onViewMagazine}
              onViewMagazineArticle={onViewMagazineArticle}
            />
          </section>

          {/* ═══ 9. PUBLICATIONS, issue archive (live magazine_issues) ════════
              Pulled live from `magazine_issues` (status='published') at runtime.
              If the query returns nothing, the section auto-hides, never
              shows placeholder issues, stays truthful. */}
          <PublicationsShowcase isMobile={isMobile} />

          {/* ═══ 10. FOR THE LISTED, proof strip + Apply CTA ═════════════════
              The hardest-working signal on the page: naming the company LWD
              keeps (Aman, Belmond, Rosewood, etc.) tells visitors what kind
              of directory this is in two seconds, no copy needed. Closes the
              page on the editorial side. */}
          <ForTheListed
            isMobile={isMobile}
            darkMode={darkMode}
            onApply={() => onViewStandard?.("apply-for-inclusion")}
          />

          {/* ═══ 11. NEWSLETTER + FOOTER ══════════════════════════════════════ */}
          <NewsletterBand />
          <DirectoryBrands onViewRegion={(countrySlug, regionSlug) => onViewRegion?.(countrySlug, regionSlug)} onViewCategory={onViewCategory} onViewUSA={onViewUSA} onViewItaly={onViewItaly} onViewCountry={onViewCountry} darkMode={darkMode} />
          </Suspense>
        </main>

        {/* Overlays, lazy, not needed until opened. No visible fallback. */}
        <Suspense fallback={null}>
          {/* Enquiry modal */}
          <EnquiryModal
            vendor={enquiryVendor}
            onClose={() => setEnquiryVendor(null)}
          />

          {/* Immersive Search overlay */}
          <ImmersiveSearch
            isOpen={immersiveOpen}
            onClose={() => { setImmersiveOpen(false); setImmersiveQuery(null); try { sessionStorage.removeItem('lwd:immersive-open'); } catch {} }}
            initialQuery={immersiveQuery}
            onViewCategory={onViewCategory}
            onViewRegionCategory={onViewRegionCategory}
          />
        </Suspense>
      </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  SectionLabel, consistent eyebrow + italic title used by the new sections
//  Mirrors the section-header treatment on /about and /apply-for-inclusion so
//  H7's voice family stays consistent with the rest of the site.
// ═══════════════════════════════════════════════════════════════════════════
function SectionLabel({ eyebrow, title, align = "center", light = false }) {
  return (
    <div style={{ textAlign: align, padding: "0 24px 56px", maxWidth: 900, margin: "0 auto" }}>
      <p style={{
        fontFamily: NU, fontSize: 10, letterSpacing: "0.4em",
        // Light-mode gold darkened from #a17d3a (3.59:1) to #8a6a30 (4.72:1)
        // against bg #f9f8f4, the original failed WCAG AA (needs 4.5:1).
        color: light ? "#8a6a30" : "rgba(211,154,33,0.85)", textTransform: "uppercase",
        margin: "0 0 18px",
      }}>
        {eyebrow}
      </p>
      <h2 style={{
        fontFamily: GD, fontSize: "clamp(32px, 4.5vw, 56px)", fontWeight: 400,
        color: light ? "#26282d" : "#f5f0e8", lineHeight: 1.05, margin: 0,
        letterSpacing: "-0.02em", fontStyle: "italic",
      }}>
        {title}
      </h2>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  AuthorityStatement, the institutional anchor.
//  Single italic line, gold rules above and below, attribution in tiny gold
//  caps. Same vocabulary as /apply-for-inclusion's authority beat. This is
//  the brand's quietest, strongest moment: it tells the visitor that this
//  is a curated standard, not a directory.
// ═══════════════════════════════════════════════════════════════════════════
function AuthorityStatement({ isMobile, darkMode }) {
  // Light-mode gold darkened from #a17d3a (3.59:1) to #8a6a30 (4.72:1)
  // against bg #f9f8f4, the original failed WCAG AA (needs 4.5:1).
  const gold = darkMode ? "#d39a21" : "#8a6a30";
  const ink  = darkMode ? "#f5f0e8" : "#26282d";
  return (
    <section style={{
      background: darkMode ? "#0a0a0a" : "#f9f8f4",
      padding: isMobile ? "100px 24px" : "160px clamp(40px, 6vw, 80px)",
    }}>
      <div style={{ maxWidth: 960, margin: "0 auto", textAlign: "center" }}>
        {/* Top gold rule, wide, fading at the ends */}
        <div aria-hidden style={{
          width: "clamp(100px, 16vw, 220px)",
          height: 1,
          margin: "0 auto 56px",
          background: `linear-gradient(to right, transparent, ${gold}, transparent)`,
        }} />

        {/* The statement, large italic serif at editorial weight */}
        <p style={{
          fontFamily: GD,
          fontStyle: "italic",
          fontSize: "clamp(26px, 3.2vw, 44px)",
          fontWeight: 400,
          lineHeight: 1.35,
          color: ink,
          margin: 0,
          letterSpacing: "-0.01em",
        }}>
          We feature a limited number of properties across each
          destination, ensuring clarity, visibility, and distinction.
        </p>

        {/* Attribution, tiny gold caps */}
        <p style={{
          fontFamily: NU,
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: gold,
          marginTop: 40,
          marginBottom: 0,
        }}>
         , The Editorial Team
        </p>

        {/* Bottom gold rule */}
        <div aria-hidden style={{
          width: "clamp(100px, 16vw, 220px)",
          height: 1,
          margin: "56px auto 0",
          background: `linear-gradient(to right, transparent, ${gold}, transparent)`,
        }} />
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PublicationsShowcase, live magazine_issues (3-4 most recent published).
//  Truthful: if the query returns nothing, the section auto-hides, never
//  invents content. Mirrors the truthful empty-state pattern used on
//  /apply-for-inclusion. Lazy-fetches on mount.
// ═══════════════════════════════════════════════════════════════════════════
function PublicationsShowcase({ isMobile }) {
  const [issues, setIssues] = useState(null); // null=loading, []=empty, [...]=data
  const scrollRef = useRef(null);

  // Scroll the carousel by one page (the visible run of covers) in either
  // direction, landing on an exact cover boundary. Desktop only, gives the
  // horizontal scroll a discoverable control for non-touch users.
  const scrollByPage = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.children[0];
    const cs = getComputedStyle(el);
    const gap = parseFloat(cs.columnGap || cs.gap) || 24;
    const perPage = isMobile ? 2 : 4;
    const cardW = firstCard ? firstCard.getBoundingClientRect().width : el.clientWidth / perPage;
    el.scrollBy({ left: dir * (cardW + gap) * perPage, behavior: "smooth" });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only columns that actually exist on magazine_issues. Earlier
        // build referenced `name` and `issue_label` which don't exist on
        // the schema, the query failed silently, the section auto-hid.
        // Limit 6, homepage is a curated editorial front page, not an
        // archive; recency-ordered (updated_at first) so the slider always
        // reflects the freshest issues.
        const { data, error } = await supabaseAnon
          .from("magazine_issues")
          .select("id, slug, title, issue_number, season, year, cover_image, published_at, created_at, updated_at")
          .eq("status", "published")
          .order("updated_at",   { ascending: false, nullsFirst: false })
          .order("published_at", { ascending: false, nullsFirst: false })
          .order("created_at",   { ascending: false })
          // `id` tiebreaker: without it, ties above are broken by DB delivery order, which
          // varies between renders and flips WHICH 6 issues land inside .limit(6) (Project B —
          // this inline query was missed by the Fix 2 audit, which covered the shared services).
          .order("id", { ascending: true })
          .limit(6);
        if (error) console.warn("[H7 PublicationsShowcase] query error:", error.message);
        if (cancelled) return;

        // Cover fallback, pull first-page image for any issue without cover
        const rows = data || [];
        const needsFallback = rows.filter(i => !i.cover_image).map(i => i.id);
        if (needsFallback.length > 0) {
          const { data: firstPages } = await supabaseAnon
            .from("magazine_issue_pages")
            .select("issue_id, image_url, thumbnail_url")
            .in("issue_id", needsFallback)
            .eq("page_number", 1);
          const coverByIssue = Object.fromEntries(
            (firstPages || []).map(p => [p.issue_id, p.image_url || p.thumbnail_url])
          );
          rows.forEach(i => {
            if (!i.cover_image && coverByIssue[i.id]) i.cover_image = coverByIssue[i.id];
          });
        }
        if (!cancelled) setIssues(rows);
      } catch {
        if (!cancelled) setIssues([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (issues === null) return null;       // suppress flash while loading
  if (issues.length === 0) return null;   // truthful, hide section entirely

  return (
    <section style={{
      // Heritage publishing green, deeper, more muted, less saturated.
      // Reads as luxury editorial archive (Penguin / library / Economist
      // shelving) rather than UI section. If still feels disconnected,
      // we'll move to black or ivory.
      background: "#172620",
      padding: isMobile ? "100px 0" : "160px 0",
      borderTop: "1px solid rgba(245,240,232,0.08)",
      borderBottom: "1px solid rgba(245,240,232,0.08)",
    }}>
      <SectionLabel eyebrow="The Magazines" title="The LWD Collection" />

      {/* Horizontal scroll carousel. Desktop: 4 large covers visible, scroll
          to reach up to 10. Mobile: 2 visible + scroll. Each issue reads as a
          collectible object, not a content card. */}
      <style>{`.h7-pubs-scroll::-webkit-scrollbar { display: none; }`}</style>
      <div style={{ position: "relative", maxWidth: 1280, margin: "0 auto" }}>
        <div ref={scrollRef} className="h7-pubs-scroll" style={{
          display: "flex",
          gap: "clamp(16px, 2.4vw, 36px)",
          padding: "0 clamp(16px, 4vw, 48px) 8px",
          overflowX: "auto",
          // mandatory for touch swipes; proximity on desktop so the arrow
          // buttons' programmatic smooth scroll isn't canceled by snap.
          scrollSnapType: isMobile ? "x mandatory" : "x proximity",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}>
          {issues.map(issue => (
            <div
              key={issue.id}
              style={{
                // 2 visible on mobile, 4 visible on desktop (accounting for the
                // inter-cover gap); the rest reached by horizontal scroll.
                flex: isMobile
                  ? "0 0 calc((100% - clamp(16px, 2.4vw, 36px)) / 2)"
                  : "0 0 calc((100% - 3 * clamp(16px, 2.4vw, 36px)) / 4)",
                minWidth: 0,
                scrollSnapAlign: "start",
              }}
            >
              <PublicationCover issue={issue} />
            </div>
          ))}
        </div>

        {/* Desktop scroll controls, only shown when there are more than 4
            issues (i.e. the row actually overflows). */}
        {!isMobile && issues.length > 4 && (
          <>
            {[-1, 1].map(dir => (
              <button
                key={dir}
                aria-label={dir < 0 ? "Previous issues" : "More issues"}
                onClick={() => scrollByPage(dir)}
                style={{
                  position: "absolute",
                  top: "50%",
                  [dir < 0 ? "left" : "right"]: "calc(clamp(16px, 4vw, 48px) - 22px)",
                  transform: "translateY(-50%)",
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  border: "1px solid rgba(211,154,33,0.55)",
                  background: "rgba(23,38,32,0.72)",
                  backdropFilter: "blur(6px)",
                  color: "#d39a21",
                  fontSize: 16,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.25s ease, border-color 0.25s ease",
                  zIndex: 2,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.18)"; e.currentTarget.style.borderColor = "#d39a21"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(23,38,32,0.72)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.55)"; }}
              >
                {dir < 0 ? "‹" : "›"}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Quiet editorial CTA, chip-style, gold ornament. Routes to
          the full Publications archive. Adds intentional close to the
          section so it reads as a destination, not a footer block. */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "clamp(48px, 6vw, 72px)" }}>
        <a
          href="/publications"
          style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            fontFamily: NU, fontSize: 11, fontWeight: 500,
            letterSpacing: "0.28em", textTransform: "uppercase",
            color: "#d39a21", textDecoration: "none",
            padding: "12px 22px",
            border: "1px solid rgba(211,154,33,0.55)",
            transition: "background 0.25s ease, border-color 0.25s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.10)"; e.currentTarget.style.borderColor = "#d39a21"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.55)"; }}
        >
          All Publications
          <span aria-hidden style={{
            width: 5, height: 5, background: "currentColor",
            transform: "rotate(45deg)", flexShrink: 0,
          }} />
        </a>
      </div>
    </section>
  );
}

function PublicationCover({ issue }) {
  const [hovered, setHovered] = useState(false);
  const img = issue.cover_image || "";
  const title = issue.title || "Issue";
  // Build a short caption: "Issue 4" if numbered, else "Spring 2026" if season/year
  const issueLabel = issue.issue_number
    ? `Issue ${issue.issue_number}`
    : (issue.season && issue.year ? `${issue.season} ${issue.year}` : "");
  const publishedDate = issue.published_at
    ? new Date(issue.published_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : "";

  return (
    <a
      href={`/publications/${issue.slug || issue.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "block", textDecoration: "none", color: "inherit",
        transition: "transform 0.45s ease",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
      }}
    >
      <div style={{
        // A4 (2480x3508) to match the rendered magazine cover exactly, a 3/4 box
        // centre-cropped the LWD masthead off the top and the venue title off the
        // bottom, leaving only the middle photo. Matching the ratio shows the whole cover.
        position: "relative", aspectRatio: "2480 / 3508", overflow: "hidden",
        background: "#1a1714",
        boxShadow: hovered
          ? "0 24px 60px rgba(0,0,0,0.40), 0 6px 16px rgba(0,0,0,0.20)"
          : "0 6px 24px rgba(0,0,0,0.20)",
        transition: "box-shadow 0.45s ease",
      }}>
        {img && (
          <img
            src={imgUrl(img, { width: 1200, quality: 78, resize: "contain" })}
            alt={title}
            loading="lazy"
            style={{
              width: "100%", height: "100%", objectFit: "cover",
              transform: hovered ? "scale(1.04)" : "scale(1)",
              transition: "transform 1.4s cubic-bezier(0.25,0.46,0.45,0.94)",
            }}
          />
        )}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(22,20,15,0.45), transparent 30%)",
          pointerEvents: "none",
        }} />
        {issueLabel && (
          <div style={{
            position: "absolute", top: 14, left: 14,
            fontFamily: NU, fontSize: 8, fontWeight: 600,
            letterSpacing: "0.28em", textTransform: "uppercase",
            color: "#d39a21",
          }}>
            {issueLabel}
          </div>
        )}
      </div>

      <div style={{ padding: "18px 4px 0" }}>
        <div style={{
          fontFamily: GD, fontSize: "clamp(15px, 1.3vw, 17px)",
          fontWeight: 400, color: "#f5f0e8", lineHeight: 1.3,
          letterSpacing: "-0.005em",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {title}
        </div>
        {publishedDate && (
          <div style={{
            fontFamily: NU, fontSize: 10, fontWeight: 500,
            letterSpacing: "0.2em", textTransform: "uppercase",
            color: "rgba(245,240,232,0.65)", marginTop: 8,
          }}>
            {publishedDate}
          </div>
        )}
      </div>
    </a>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ForTheListed, proof strip + Apply for Inclusion CTA.
//  Lifted verbatim from H4, the single hardest-working section in that
//  build. Naming the company LWD keeps (Aman, Belmond, Rosewood, etc.) is
//  the fastest brand-position signal a visitor sees on the homepage.
//  Closes the page on the editorial / institutional side.
// ═══════════════════════════════════════════════════════════════════════════
function ForTheListed({ isMobile, onApply, darkMode }) {
  const proof = ["Aman", "Four Seasons", "Ritz-Carlton", "Mandarin Oriental", "Rosewood", "Belmond", "Six Senses"];
  // Light-mode gold/eyebrow darkened from #a17d3a (3.59:1) to #8a6a30
  // (4.72:1) against bg #f9f8f4, the original failed WCAG AA (needs 4.5:1).
  const gold     = darkMode ? "#d39a21" : "#8a6a30";
  const eyebrow  = darkMode ? "rgba(211,154,33,0.85)" : "#8a6a30";
  const ink      = darkMode ? "#f5f0e8" : "#26282d";
  const muted    = darkMode ? "rgba(245,240,232,0.55)" : "#5a6068";
  const hoverTxt = darkMode ? "#0a0806" : "#ffffff";
  return (
    <section style={{
      background: darkMode ? "#000000" : "#f9f8f4",
      padding: isMobile ? "100px 24px" : "160px 80px",
      textAlign: "center",
      borderTop: darkMode ? "1px solid rgba(245,240,232,0.05)" : "1px solid rgba(0,0,0,0.08)",
    }}>
      <p style={{
        fontFamily: NU, fontSize: 9, letterSpacing: "0.4em",
        color: eyebrow, textTransform: "uppercase",
        margin: "0 0 36px",
      }}>
        In good company
      </p>

      {/* Proof strip, italic gold-dim names, no logos. */}
      <div style={{
        display: "flex", justifyContent: "center", flexWrap: "wrap",
        gap: isMobile ? 16 : 36, marginBottom: 60,
      }}>
        {proof.map((name) => (
          <span key={name} style={{
            fontFamily: GD, fontSize: isMobile ? 13 : 16,
            fontStyle: "italic", color: muted,
            letterSpacing: "0.05em",
          }}>
            {name}
          </span>
        ))}
      </div>

      {/* Sharper, more selective. "If your work belongs in this
          directory…" was passive. States the standard as a fact, then
          invites the application, rather than framing entry as needing
          an invitation, which discouraged people from reaching out. */}
      <h2 style={{
        fontFamily: GD, fontSize: "clamp(36px, 5.2vw, 64px)",
        fontStyle: "italic", fontWeight: 400, color: ink,
        lineHeight: 1.1, margin: "0 auto 24px", maxWidth: 820,
        letterSpacing: "-0.02em",
      }}>
        We list the exceptional.
      </h2>
      <p style={{
        fontFamily: NU, fontSize: "clamp(13px, 1.4vw, 16px)",
        color: muted,
        margin: "0 auto 56px", maxWidth: 520,
        lineHeight: 1.7, fontWeight: 300, letterSpacing: "0.02em",
      }}>
        Tell us about your business, an application starts the conversation.
      </p>

      {/* CTA in the chip vocabulary, outlined gold + diamond ornament.
          Same register as the Aura CTA above and the masthead's Apply
          chip. Reads as editorial gesture, not conversion pill. */}
      <button
        onClick={onApply}
        style={{
          display: "inline-flex", alignItems: "center", gap: 12,
          background: "transparent",
          border: `1px solid ${gold}`,
          color: gold,
          padding: "18px 44px",
          fontFamily: NU, fontSize: 11,
          letterSpacing: "0.28em", textTransform: "uppercase",
          cursor: "pointer",
          transition: "background 0.3s, color 0.3s",
          borderRadius: 1, fontWeight: 500,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = gold; e.currentTarget.style.color = hoverTxt; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = gold; }}
      >
        Apply for Inclusion
        <span aria-hidden style={{
          width: 5, height: 5, background: "currentColor",
          transform: "rotate(45deg)", flexShrink: 0,
        }} />
      </button>
    </section>
  );
}
